import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../../assets/css/--component-checkout.css";
import PaypalIcon from "../../assets/images/paypal-icon.png";
import Breadcrumbs from "../../components/BreadCrumbs";
import { cartCheckout, deleteCart, getAccessToken, getCheckoutDetails, getProductCart } from "../../helper/backend-methods";
import Loader from "../../components/Loader";
import AppLoader from "../../components/AppLoader";
import CheckoutModal from "./checkoutModal";
import { GlobalContext } from "../../context";
import { getUserCards } from "../../helper/backend-methods";
import { CardImage } from "../../components/CardImage";
import { customToast } from "../../components/customToast";
import { use } from "react";
import { set } from "lodash";

function CheckoutPage() {
  // State management
  const history = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState("creditCard");
  const [saveDetails, setSaveDetails] = useState(true);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const [showModal, setModal] = useState(false);
  const [tax, setTax] = useState(0);
  const { cartCount, updateCartCount, cartDrawer, updateCartDrawer } = useContext(GlobalContext);
  const [pickupTime, setPickupTime] = useState("");
  const [notifyReady, setNotifyReady] = useState(false);
  const [viewAddons, setViewAddons] = useState(false);
  const [cardFieldsValidity, setCardFieldsValidity] = useState({
    cardNumber: false,
    expiration: false,
    cvv: false,
  });
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [useNewCard, setUseNewCard] = useState(true);
  const [cardFormReady, setCardFormReady] = useState(false);
  // Once the "Add New Card" fields have been mounted, keep them mounted for good —
  // toggling useNewCard afterwards only shows/hides them via CSS instead of
  // unmounting, since destroying and recreating the hosted iframe fields is what
  // caused the slow reload every time a saved card was picked and then un-picked.
  const [showCardInputs, setShowCardInputs] = useState(useNewCard);
  const cardFormRef = useRef(null);
  const cardFormInstance = useRef(null);
  const cardFormInitStarted = useRef(false);
  const cartItemsRef = useRef([]);
  const formDataRef = useRef({});
  const paymentMethodsRef = useRef([]);
  const accessTokenRef = useRef("");
  const saveDetailsRef = useRef(true);
  const checkoutSuccessRef = useRef(false);
  const [hiddenAddons, setHiddenAddons] = useState(new Set());
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    orderNotes: "",
  });

  // Validation states for contact information only
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    cardNumber: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
  });

  const [isTouched, setIsTouched] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    email: false,
    cardNumber: false,
    expiryMonth: false,
    expiryYear: false,
    cvv: false,
  });
  const breadcrumbItems = [
    { title: "Home", link: "/" },
    { title: "Checkout", link: "" },
  ];

  // Validation functions for contact information
  const validateFirstName = (value) => {
    if (!value.trim()) {
      return "First name is required";
    }
    if (value.trim().length < 2) {
      return "First name must be at least 2 characters";
    }
    if (value.trim().length > 50) {
      return "First name must be less than 50 characters";
    }
    if (!/^[a-zA-Z\s'-]+$/.test(value)) {
      return "First name can only contain letters";
    }
    return "";
  };

  const validateLastName = (value) => {
    if (!value.trim()) {
      return "Last name is required";
    }
    if (value.trim().length < 2) {
      return "Last name must be at least 2 characters";
    }
    if (value.trim().length > 50) {
      return "Last name must be less than 50 characters";
    }
    if (!/^[a-zA-Z\s'-]+$/.test(value)) {
      return "Last name can only contain letters";
    }
    return "";
  };

  const validatePhone = (value) => {
    if (!value || !value.trim()) {
      return "Phone number is required";
    }

    const cleanPhone = value.replace(/\D/g, "");

    const isValid = cleanPhone.length === 10 || (cleanPhone.length === 11 && cleanPhone.startsWith("1"));

    if (!isValid) {
      return "Phone number must be 10 digits";
    }
    return "";
  };

  const validateEmail = (value) => {
    if (!value.trim()) {
      return "Email is required";
    }
    if (value.trim().length > 50) {
      return "Email must be less than 50 characters";
    }
    if (value && value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "Please enter a valid email address";
      }
    }
    return "";
  };

  const validateCardNumber = (value) => {
    if (!value || !value.trim()) {
      return "Card number is required";
    }

    // Remove spaces for validation
    const digitsOnly = value.replace(/\s/g, "");

    if (digitsOnly.length !== 16) {
      return "Card number must be 16 digits";
    }
    return "";
  };

  const validateExpiryMonth = (value) => {
    if (!value.trim()) {
      return "Expiry month is required";
    }
    if (value.trim().length !== 2) {
      return "Expiry month must be 2 digits";
    }
    return "";
  };

  const validateExpiryYear = (value) => {
    if (!value.trim()) {
      return "Expiry year is required";
    }
    if (value.trim().length !== 2) {
      return "Expiry year must be 2 digits";
    }
    return "";
  };

  const validateCVV = (value) => {
    if (!value.trim()) {
      return "CVV is required";
    }
    if (value.trim().length !== 3) {
      return "CVV must be 3 digits";
    }
    return "";
  };

  const isContactInfoValid = () => {
    const contactValid =
      !errors.firstName &&
      !errors.lastName &&
      !errors.phone &&
      !errors.email &&
      !errors.orderNotes &&
      formData.firstName?.trim() !== "" &&
      formData.lastName?.trim() !== "" &&
      formData.phone?.trim() !== "" &&
      formData.email?.trim() !== "";

    if (paymentMethod === "paypal") {
      return contactValid;
    }

    if (selectedCard !== null) {
      return contactValid;
    }

    if (useNewCard) {
      // Check if all card fields are valid
      const allCardFieldsValid = cardFieldsValidity.cardNumber && cardFieldsValidity.expiration && cardFieldsValidity.cvv;

      return contactValid && allCardFieldsValid;
    }

    return contactValid;
  };

  // Handle form changes with validation for contact info only
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Only validate contact information fields
    if (["firstName", "lastName", "phone", "email", "orderNotes"].includes(name)) {
      // Mark field as touched
      setIsTouched((prev) => ({
        ...prev,
        [name]: true,
      }));

      // Validate field
      let error = "";
      switch (name) {
        case "firstName":
          error = validateFirstName(value);
          break;
        case "lastName":
          error = validateLastName(value);
          break;
        case "phone":
          error = validatePhone(value);
          break;
        case "email":
          error = validateEmail(value);
          break;
        default:
          break;
      }

      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  // Format phone number
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");

    // Limit to 10 digits for US phone numbers
    const limited = cleaned.slice(0, 10);

    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `+1(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else {
      return `+1(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6, 10)}`;
    }
  };

  // Format card number
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(" ") : cleaned;
  };

  const handlePlaceOrder = (e) => {
    setLoading(true);
    e.preventDefault();

    // Mark all contact fields as touched on submit
    const allTouched = {
      firstName: true,
      lastName: true,
      phone: true,
      email: true,
    };
    setIsTouched(allTouched);

    // Validate all contact fields
    const newErrors = {
      firstName: validateFirstName(formData.firstName),
      lastName: validateLastName(formData.lastName),
      phone: validatePhone(formData.phone),
      email: validateEmail(formData.email),
    };

    setErrors(newErrors);

    // Check if contact info is valid and terms are agreed
    const isContactValid = !newErrors.firstName && !newErrors.lastName && !newErrors.phone && !newErrors.email && !newErrors.orderNotes;

    if (!agreeTerms) {
      alert("Please agree to the terms and conditions");
      setLoading(false);
      return;
    }

    if (!isContactValid) {
      console.log("Form has errors");
      setLoading(false);
      return;
    }

    if (cardFormInstance.current && selectedCard === null) {
      cardFormInstance.current.submitForm();
    } else if (selectedCard !== null) {
      let selectedCardObj = savedCards.find((card) => card.CardId === selectedCard);
      handleSubmit(selectedCardObj);
    } else {
      // Scroll to first error
      const firstError = Object.keys(newErrors).find((key) => newErrors[key]);
      if (firstError) {
        document.querySelector(`[name="${firstError}"]`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  const saveCardDetails = (val) => {
    setSaveDetails(val);
    saveDetailsRef.current = val;
  };

  const handleSubmit = async (resp) => {
    let cardPayload = null;
    if (selectedCard !== null) {
      cardPayload = resp;
    } else {
      cardPayload = {
        type: resp.details.cardCategory || "CREDIT",
        token: resp.paymentReference,
        expiry_Month: parseInt(resp.details.expiryMonth),
        expiry_Year: parseInt(resp.details.expiryYear),
        expires_On: `${resp.details.expiryMonth}/${resp.details.expiryYear}`,
        scheme: resp.details.scheme || resp.details.cardType,
        card_Type: resp.details.cardType,
        last4: resp.details.cardLast4,
        bin: resp.details.cardBin,
      };
    }

    const payload = {
      email: formDataRef.current.email,
      ShippingAddressId: 1,
      BillingAddressId: 1,
      cardDetails: cardPayload,
      UcpPaymentRequest: {
        country: "US",
        reference: !selectedCard ? resp.details.reference : "",
        payment_method: {
          entry_mode: "ECOM",
          id: resp.paymentReference || resp.token,
        },
      },
      PaymentMethod: paymentMethodsRef.current[0].Name,
      saveCard: saveDetailsRef.current,
      CartItems: cartItemsRef.current.map((item) => ({
        ProductId: item.Id,
        StockQuantity: item.Quantity,
      })),
      OrderNote: formData.orderNotes,
    };
    setLoadingButton(true);

    cartCheckout(payload)
      .then(({ data }) => {
        if (data.Status === true) {
          setLoadingButton(false);
          setLoading(false);
          checkoutSuccessRef.current = true;
          setModal(true);
          updateCartCount(0);
          updateCartDrawer();
        } else {
          setLoadingButton(false);
          customToast.error("Checkout", data.Message || "Checkout failed");
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setLoadingButton(false);
        setLoading(false);
        customToast.error("Checkout", error.message || "Checkout failed");
        setLoading(false);
      });
  };

  // Handle phone input with formatting
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    handleInputChange({ target: { name: "phone", value: formatted } });
  };

  // Handle card number input with formatting
  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setFormData((prev) => ({ ...prev, cardNumber: formatted }));
    setIsTouched((prev) => ({ ...prev, cardNumber: true }));
    setErrors((prev) => ({ ...prev, cardNumber: validateCardNumber(formatted.trim()) }));
  };

  // Handle CVV input
  const handleCVVChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
    setFormData((prev) => ({ ...prev, cvv: value }));
    setIsTouched((prev) => ({ ...prev, cvv: true }));
    setErrors((prev) => ({ ...prev, cvv: validateCVV(value) }));
  };

  // Handle expiry month/year change
  const handleMonthExpiryChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 2);
    setFormData((prev) => ({ ...prev, expiryMonth: value }));
    setIsTouched((prev) => ({ ...prev, expiryMonth: true }));
    setErrors((prev) => ({ ...prev, expiryMonth: validateExpiryMonth(value) }));
  };

  const handleYearExpiryChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateExpiryYear(value) }));
  };

  const _getCheckOutItems = () => {
    getCheckoutDetails().then(({ data }) => {
      if (data.Status) {
        if (data.Data.Items.length === 0 && !checkoutSuccessRef.current) {
          history("/menu");
          return;
        }
        setCartItems(data.Data.Items);
        setTax(data.Data.TotalTax);
        paymentMethodsRef.current = data.Data.PaymentMethod || [];
        const user = data.Data.UserInfo;
        if (user) {
          const parsedUser = user;
          const nameParts = (parsedUser.FullName || "").trim().split(/\s+/);
          setFormData({
            firstName: nameParts.slice(0, -1).join(" ") || nameParts[0] || "",
            lastName: nameParts.length > 1 ? nameParts[nameParts.length - 1] : "",
            phone: parsedUser.Phone,
            email: parsedUser.Email,
          });
        }
      } else {
        setCartItems([]);
        setTax(0);
        !checkoutSuccessRef.current && history("/menu");
        setLoading(false);
        return;
      }
    });
  };

  useEffect(() => {
    // Only preload the Global Payments hosted fields once "Add New Card" is actually
    // selected, and only ever do it once — re-running this on every toggle used to
    // tear down and rebuild the iframe fields each time, which is what made them feel
    // slow. cardFormInitStarted guards against re-initializing on later toggles.
    if (!useNewCard || cardFormInitStarted.current) return;
    cardFormInitStarted.current = true;

    let cardForm = null;
    let cancelled = false;
    let pollId = null;

    const init = () => {
      if (cancelled) return;

      if (!window.GlobalPayments) {
        pollId = setTimeout(init, 100);
        return;
      }

      getAccessToken()
      .then(({ data }) => {
        if (!data) return;

        const accessToken = `${data.token}`;
        accessTokenRef.current = accessToken;
        window.GlobalPayments.configure({
          accessToken,
          env: "sandbox",
        });
      })
      .finally(() => {
        cardForm = window.GlobalPayments.ui.form({
          fields: {
            "card-number": {
              placeholder: "XXXX XXXX XXXX XXXX",
              target: "#card-number",
            },
            "card-expiration": {
              placeholder: "MM / YYYY",
              target: "#card-expiration",
            },
            "card-cvv": {
              placeholder: "CVV",
              target: "#card-cvv",
            },
            // "card-holder-name": {
            //   placeholder: "Jane Smith",
            //   target: "#card-holder-name"
            // },
            submit: {
              text: "Pay Now", // Change the text of the button
              target: "#submit-button",
              value: "Pay Now",
              display: "hidden",
            },
          },
          styles: {
            input: {
              border: "1px solid #d1d5db",
              height: "42px",
              padding: "0px 12px",
              margin: "0px",
              "margin-top": "5px",
              "font-size": "14px",
              "font-weight": "500",
              "font-family": "Sofia Sans, sans-serif",
              color: "#6b7280",
              outline: "none",
              "border-radius": "5px",
              background: "#fff",
            },
            "input:focus": {
              outline: "none",
              "box-shadow": "none",
              border: "1px solid #2563eb",
            },
          },
        });

        cardForm.ready(() => {
          console.log("Registration of all credit card fields occurred");
          setCardFormReady(true);
        });

        cardForm.on("token-success", (resp) => {
          // add payment token to form as a hidden input
          const token = document.createElement("input");
          token.type = "hidden";
          token.name = "payment-reference";
          token.value = resp.paymentReference;
          handleSubmit(resp);
        });

        cardForm.on("token-error", (resp) => {
          console.log("Registration of Card Number occurred", resp);
          // TODO: Add your error handling
        });

        // field-level event handlers. example:
        cardForm.on("card-number", "register", () => {
          console.log("Registration of Card Number occurred");
        });

        cardForm.on("card-number-test", (resp) => {
          setErrors((prev) => ({ ...prev, cardNumber: resp.valid ? "" : "Invalid Card Number" }));
          setIsTouched((prev) => ({ ...prev, cardNumber: true }));
          setCardFieldsValidity((prev) => ({ ...prev, cardNumber: resp.valid }));
          console.log("Card Number Valid:", resp.valid);
        });

        cardForm.on("card-expiration-test", (resp) => {
          setErrors((prev) => ({ ...prev, expiryMonth: resp.valid ? "" : "Invalid Expiry Date" }));
          setIsTouched((prev) => ({ ...prev, expiryMonth: true }));
          setCardFieldsValidity((prev) => ({ ...prev, expiration: resp.valid }));
          console.log("Expiration Date Valid:", resp.valid);
        });

        cardForm.on("card-cvv-test", (resp) => {
          setErrors((prev) => ({ ...prev, cvv: resp.valid ? "" : "Invalid CVV" }));
          setIsTouched((prev) => ({ ...prev, cvv: true }));
          setCardFieldsValidity((prev) => ({ ...prev, cvv: resp.valid }));
          console.log("CVV Valid:", resp.valid);
        });

        cardForm.on("token-error", (resp) => {
          console.log("Card validation failed:", resp);
          customToast.error("Card Error", resp.error?.message || "Invalid card details");
          setLoadingButton(false);
        });

        cardFormInstance.current = cardForm;
      });
    };

    init();

    return () => {
      cancelled = true;
      if (pollId) clearTimeout(pollId);
    };
  }, [useNewCard]);

  useEffect(() => {
    _getCheckOutItems();
  }, [cartCount]);

  useEffect(() => {
    cartItemsRef.current = cartItems;
    formDataRef.current = formData;
  }, [cartItems]);
  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + item.Total, 0);
  //const deliveryFee = 3.85;
  const grandTotal = subtotal + tax; //deliveryFee;

  const formatExpiryDate = (month, year) => {
    const formattedMonth = month.toString().padStart(2, "0");
    const formattedYear = year.toString().slice(-2);
    return `${formattedMonth}/${formattedYear}`;
  };

  const getCardBadge = (type) => {
    switch (type.toLowerCase()) {
      case "visa":
        return "VISA";
      case "mastercard":
        return "MC";
      case "discover":
        return "DISC";
      case "amex":
        return "AMEX";
      default:
        return type.toUpperCase();
    }
  };

  const getCardBadgeClass = (type) => {
    switch (type.toLowerCase()) {
      case "visa":
        return "visa";
      case "mastercard":
        return "mastercard";
      case "discover":
        return "discover";
      case "amex":
        return "amex";
      default:
        return "visa";
    }
  };

  useEffect(() => {
    getUserCards().then(({ data }) => {
      if (data.Status) {
        setSavedCards(data.Data.sort((a, b) => (a.Selected ? -1 : 1)));
        const defaultCard = data.Data.find((card) => card.Selected);
        if (defaultCard) {
          setSelectedCard(defaultCard.CardId);
        }
        if (data.Data.length > 0) {
          setUseNewCard(false);
        }
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!useNewCard) {
      setCardFieldsValidity({ cardNumber: false, expiration: false, cvv: false });
    } else {
      setShowCardInputs(true);
    }
  }, [useNewCard]);

  return (
    <div id="checkout">
      {loading && (
        <div className="loader-center checkout-loader">
          <AppLoader text="Preparing your checkout…" />
        </div>
      )}
      <Breadcrumbs pageTile="Store" breadcrumbItems={breadcrumbItems} />
      <div className="wrapper">
        <div className="section-header w-100">
          <h1 className="checkout-title">Delivery & Payment Information</h1>
        </div>
        <form onSubmit={handlePlaceOrder} className="checkout-container" id="payment-form">
          <div className="checkout-container">
            {/* Left Column - Contact & Payment Info */}
            <div className="left-checkout">
              {/* Contact Information */}
              <div className="personal-information">
                <div className="card">
                  <div className="card-heading">
                    <h2 className="contact-hd">Contact Information</h2>
                    <span className="separator-checkout separator my-3" />
                  </div>
                  <div className="d-flex justify-between flex-wrap contact-info">
                    {/* First Name */}
                    <div className="checkout-form-content mt-2">
                      <label className="fw-500 fs-14 m-0">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        onBlur={() => setIsTouched((prev) => ({ ...prev, firstName: true }))}
                        maxLength="50"
                        disabled={true}
                        placeholder="Your First Name"
                        className={errors.firstName && isTouched.firstName ? "input-error" : ""}
                      />
                      {errors.firstName && isTouched.firstName && <div className="error-message">{errors.firstName}</div>}
                    </div>

                    {/* Last Name */}
                    <div className="checkout-form-content mt-2">
                      <label className="fw-500 fs-14 m-0">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        onBlur={() => setIsTouched((prev) => ({ ...prev, lastName: true }))}
                        maxLength="50"
                        disabled={true}
                        placeholder="Your Last Name"
                        className={errors.lastName && isTouched.lastName ? "input-error" : ""}
                      />
                      {errors.lastName && isTouched.lastName && <div className="error-message">{errors.lastName}</div>}
                    </div>

                    {/* Phone Number */}
                    <div className="checkout-form-content mt-2">
                      <label className="fw-500 fs-14 m-0">
                        Phone <span className="text-danger">*</span>
                      </label>
                      <div>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          onBlur={() => setIsTouched((prev) => ({ ...prev, phone: true }))}
                          maxLength="15"
                          disabled={true}
                          placeholder="(XXX) XXX-XXXX"
                          className={errors.phone && isTouched.phone ? "input-error" : ""}
                        />
                      </div>
                      {errors.phone && isTouched.phone && <div className="error-message">{errors.phone}</div>}
                      <div className="phone-hint">US format: (XXX) XXX-XXXX</div>
                    </div>

                    {/* Email */}
                    <div className="checkout-form-content mt-2">
                      <label className="fw-500 fs-14 m-0">Email</label>
                      <div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          onBlur={() => setIsTouched((prev) => ({ ...prev, email: true }))}
                          disabled={true}
                          maxLength="50"
                          placeholder="your.email@example.com"
                          className={errors.email && isTouched.email ? "input-error" : ""}
                        />
                      </div>
                      {errors.email && isTouched.email && <div className="error-message">{errors.email}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Pickup Details */}
              <div className="shipping-information">
                <div className="card">
                  <div className="card-heading">
                    <div>
                      <h2 className="contact-hd">Order Notes (Optional)</h2>
                      <span className="separator-checkout separator my-3" />
                      {/* <p className="fs-14 text-gray-400 mt-2">Choose when you'd like to pick up your order.</p> */}
                    </div>
                  </div>

                  {/* <div className="checkout-form-content checkout-form-pickupTime mt-2">
                  <div>
                    <span className="pickup-schedule">Schedule Your Order</span>
                  </div>
                  <div>
                    <span className="pickup-schedule-US">All times in US/Central</span>
                  </div>
                  <select
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="checkout-select"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #d6d5d5",
                      borderRadius: "5px",
                      fontSize: "14px",
                      color: "#393939",
                      backgroundColor: "#fff",
                      outline: "none",
                    }}
                  >
                    <option value="">Select Desired Pickup Time</option>
                    <option value="09:00">9:00 AM</option>
                    <option value="10:00">10:00 AM</option>
                    <option value="11:00">11:00 AM</option>
                    <option value="12:00">12:00 PM</option>
                    <option value="13:00">1:00 PM</option>
                    <option value="14:00">2:00 PM</option>
                    <option value="15:00">3:00 PM</option>
                    <option value="16:00">4:00 PM</option>
                  </select>
                </div> */}

                  {/* <div className="d-flex align-items-center mt-3">
                  <input
                    type="checkbox"
                    id="notifyReady"
                    checked={notifyReady}
                    onChange={(e) => setNotifyReady(e.target.checked)}
                    className="input-checkbox"
                    style={{ marginRight: "10px" }}
                  />
                  <label htmlFor="notifyReady" className="fw-400 fs-14 text-gray-500">
                    Notify me when order is ready for pickup
                  </label>
                </div> */}
                  <div className="mt-2">
                    {/* <span className="separator-checkout separator my-3" /> */}
                    {/* <h3 className="fs-18 fw-400 text-gray-500 mb-4">
                    Order Notes <span className="optional-notes">(Optional)</span>
                  </h3> */}
                    <textarea
                      name="orderNotes"
                      value={formData.orderNotes}
                      onChange={handleInputChange}
                      placeholder="Add a note for our kitchen..."
                      rows="3"
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "1px solid #d6d5d5",
                        borderRadius: "5px",
                        fontSize: "14px",
                        color: "#393939",
                        backgroundColor: "#fff",
                        outline: "none",
                        resize: "vertical",
                        fontFamily: "inherit",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="payment-method">
                <div className="card">
                  <div className="card-heading">
                    <h2 className="contact-hd">Payment Method</h2>
                    <span className="separator-checkout separator my-3" />
                  </div>

                  <div className="payment-card-toggle-btn mb-2">
                    <span className="payment-cardinfo">
                      <input
                        onChange={() => setPaymentMethod("creditCard")}
                        className="input-radio"
                        type="radio"
                        name="paymentOption"
                        value="creditCard"
                        id="paymentOption1"
                        checked={paymentMethod === "creditCard"}
                      />
                      <label className="fw-500 fs-14 cardinfo-text" htmlFor="paymentOption1">
                        <svg
                          className="method-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          fill="currentColor"
                        >
                          <path d="M3.00488 2.99979H21.0049C21.5572 2.99979 22.0049 3.4475 22.0049 3.99979V19.9998C22.0049 20.5521 21.5572 20.9998 21.0049 20.9998H3.00488C2.4526 20.9998 2.00488 20.5521 2.00488 19.9998V3.99979C2.00488 3.4475 2.4526 2.99979 3.00488 2.99979ZM20.0049 11.9998H4.00488V18.9998H20.0049V11.9998ZM20.0049 7.99979V4.99979H4.00488V7.99979H20.0049Z"></path>
                        </svg>
                        Pay by Credit Card
                      </label>
                    </span>
                    {/* <span className="payment-cardinfo">
                    <input
                      onChange={() => setPaymentMethod("paypal")}
                      className="input-radio"
                      type="radio"
                      name="paymentOption"
                      value="paypal"
                      id="paymentOption2"
                      checked={paymentMethod === "paypal"}
                    />
                    <label className="fw-500 fs-14 cardinfo-text" htmlFor="paymentOption2">
                      <img className="paypal-img-icon method-icon" src={PaypalIcon} alt="paypalIcon" />
                      PayPal
                    </label>
                  </span> */}
                  </div>

                  {paymentMethod === "creditCard" ? (
                    <div className="payment-option active">
                      <div className="card-payment-options">
                        <div className="saved-cards-section">
                          {savedCards.map((card) => (
                            <div
                              key={card.CardId}
                              className={`payment-card-selectable ${selectedCard === card.CardId ? "selected" : ""}`}
                              onClick={() => {
                                setSelectedCard(card.CardId);
                                setUseNewCard(false);
                              }}
                            >
                              <input
                                type="radio"
                                name="cardSelection"
                                checked={selectedCard === card.CardId}
                                onChange={() => {
                                  setSelectedCard(card.CardId);
                                }}
                              />
                              <span className={`card-badge ${getCardBadgeClass(card.Scheme)}`}>
                                <CardImage type={card.Scheme} />
                              </span>
                              <div className="card-details">
                                <h6>
                                  {card.Scheme} ending in **** {card.Last4}
                                </h6>
                                <span>Expires {formatExpiryDate(card.ExpiryMonth, card.ExpiryYear)}</span>
                              </div>
                              {card.Selected && <span className="default-badge">Default</span>}
                            </div>
                          ))}

                          {/* Add New Card Option */}
                          <div
                            className={`payment-card-selectable add-new-card ${useNewCard ? "selected" : ""}`}
                            onClick={() => {
                              setUseNewCard(true);
                              setSelectedCard(null);
                            }}
                          >
                            <input type="radio" name="cardSelection" checked={useNewCard} onChange={() => setUseNewCard(true)} />
                            <div className="card-details">
                              <h6>
                                <i className="ri-add-line"></i>
                                Add New Card
                              </h6>
                            </div>
                          </div>
                        </div>

                        {/* Rendered once useNewCard is first true, then kept mounted for good —
                            only visibility (via CSS) tracks useNewCard afterwards. */}
                        {showCardInputs && (
                          <div className={`new-card-inputs ${useNewCard ? "" : "card-hidden"}`}>
                            <div className={`new-card ${cardFormReady ? "" : "card-fields-loading"}`}>
                              {/* Stays mounted and fades out via CSS instead of being yanked out of the
                                  DOM the instant cardFormReady flips, so the reveal feels smooth. */}
                              <div className={`card-fields-skeleton ${cardFormReady ? "skeleton-hidden" : ""}`}>
                                <div className="skeleton-line skeleton-label" />
                                <div className="skeleton-line skeleton-input" />
                                <div className="card-row">
                                  <div className="card-row-item">
                                    <div className="skeleton-line skeleton-label" />
                                    <div className="skeleton-line skeleton-input" />
                                  </div>
                                  <div className="card-row-item">
                                    <div className="skeleton-line skeleton-label" />
                                    <div className="skeleton-line skeleton-input" />
                                  </div>
                                </div>
                              </div>
                              {/* <label htmlFor="card-holder-name">Cardholder Name</label> */}
                              <div id="card-holder-name"></div>
                              {errors.cardHolderName && isTouched.cardHolderName && (
                                <div className="error-message">{errors.cardHolderName}</div>
                              )}
                              <label htmlFor="card-number">Card Number</label>
                              <div id="card-number"></div>
                              {errors.cardNumber && isTouched.cardNumber && <div className="error-message">{errors.cardNumber}</div>}

                              <div className="card-row">
                                <div className="card-row-item">
                                  <label htmlFor="card-expiration">Expiry Date</label>
                                  <div id="card-expiration"></div>
                                  {errors.expiryMonth && isTouched.expiryMonth && <div className="error-message">{errors.expiryMonth}</div>}
                                </div>
                                <div className="card-row-item">
                                  <label htmlFor="card-cvv">CVV</label>
                                  <div id="card-cvv"></div>
                                  {errors.cvv && isTouched.cvv && <div className="error-message">{errors.cvv}</div>}
                                </div>
                              </div>
                              <div id="submit-button"></div>

                              {/* <div className="checkout-form-pickupTime checkout-form-content mt-2 w-100 position-relative">
                          <label className="fw-500 fs-14 m-0">
                            Card Number <span className="text-danger">*</span>
                          </label>
                          <div>
                            <input
                              type="text"
                              name="cardNumber"
                              value={formData.cardNumber}
                              onChange={handleCardNumberChange}
                              placeholder="XXXX XXXX XXXX XXXX"
                              maxLength="19"
                            />
                          </div>
                          {errors.cardNumber && isTouched.cardNumber && <div className="error-message">{errors.cardNumber}</div>}
                        </div>

                        <div className="payment-cardInfo-detail">
                          <div className="checkout-detail-form checkout-form-content mt-2">
                            <label className="fw-500 fs-14 m-0">
                              Month <span className="text-danger">*</span>
                            </label>
                            <select
                              name="expiryMonth"
                              value={formData.expiryMonth}
                              onChange={handleMonthExpiryChange}
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "1px solid #d6d5d5",
                                borderRadius: "5px",
                                fontSize: "14px",
                                color: "#393939",
                                backgroundColor: "#fff",
                                outline: "none",
                              }}
                            >
                              <option value="">MM</option>
                              {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                                <option key={month} value={month.toString().padStart(2, "0")}>
                                  {month.toString().padStart(2, "0")}
                                </option>
                              ))}
                            </select>
                            {errors.expiryMonth && isTouched.expiryMonth && <div className="error-message">{errors.expiryMonth}</div>}
                          </div>

                          <div className="checkout-detail-form checkout-form-content mt-2">
                            <label className="fw-500 fs-14 m-0">
                              Year <span className="text-danger">*</span>
                            </label>
                            <select
                              name="expiryYear"
                              value={formData.expiryYear}
                              onChange={handleYearExpiryChange}
                              style={{
                                width: "100%",
                                padding: "10px 12px",
                                border: "1px solid #d6d5d5",
                                borderRadius: "5px",
                                fontSize: "14px",
                                color: "#393939",
                                backgroundColor: "#fff",
                                outline: "none",
                              }}
                            >
                              <option value="">YY</option>
                              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map((year) => (
                                <option key={year} value={year.toString().slice(-2)}>
                                  {year.toString().slice(-2)}
                                </option>
                              ))}
                            </select>
                            {errors.expiryYear && isTouched.expiryYear && <div className="error-message">{errors.expiryYear}</div>}
                          </div>

                          <div className="checkout-detail-form checkout-form-content mt-2">
                            <label className="fw-500 fs-14 m-0">
                              CVV <span className="text-danger">*</span>
                            </label>
                            <div>
                              <input
                                type="text"
                                name="cvv"
                                value={formData.cvv}
                                onChange={handleCVVChange}
                                placeholder="CVV"
                                maxLength="3"
                              />
                            </div>
                            {errors.cvv && isTouched.cvv && <div className="error-message">{errors.cvv}</div>}
                          </div>
                        </div> */}

                              <div className="alertBox-save-consent">
                                <div className="save-consent d-flex align-items-center mt-3">
                                  <input
                                    type="checkbox"
                                    className="input-checkbox"
                                    name="saveConsent"
                                    id="saveConsent"
                                    value={true}
                                    checked={saveDetails}
                                    onChange={(e) => saveCardDetails(e.target.checked)}
                                  />
                                  <label className="fs-14 fw-500 text-gray-500" htmlFor="saveConsent">
                                    Save Contact and Payment Details
                                  </label>
                                </div>
                                <p className="fs-12 fw-400 text-gray-400 mt-1">Create an account for faster checkout on future orders.</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="payment-option active">
                      <div className="card-payment-options">
                        <div className="single-payment-card d-block" style={{ textAlign: "center", padding: "20px" }}>
                          <img
                            src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg"
                            alt="PayPal"
                            style={{ maxWidth: "150px", margin: "0 auto 20px" }}
                          />
                          <p className="fs-14 fw-400 text-gray-400">You will be redirected to PayPal to complete your payment.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="right-checkout">
              <div className="card">
                <div className="checkout-product-heading">
                  <h2 className="contact-hd">Your Order</h2>
                  <span className="separator-checkout separator my-3" />

                  <div id="checkoutProducts">
                    {cartItems.length > 0 ? (
                      cartItems.map((item, index) => (
                        <div key={index} className="checkout-product">
                          <div className="checkout-list">
                            <div className="right-checkout-product">
                              <div className="product-title">
                                <p className="text-gray-500 fw-500 fs-14 m-0">
                                  {item.Quantity} x {item.ProductName}
                                </p>
                              </div>
                            </div>

                            <div className="ror-checkout-product">
                              <div className="checkout-product-price">
                                <h2 className="text-right fs-14 fw-500 text-secondary m-0">
                                  <span className="fs-14 fw-500 text-gray-500">$</span> {item.Total.toFixed(2)}
                                </h2>
                              </div>
                            </div>
                          </div>
                          {item.ProductAttributeDetails.Customizations && item.ProductAttributeDetails.Customizations.length > 0 && (
                                  <div className="product-customizations">
                                    {item.ProductAttributeDetails.Customizations.map((customization, index) => (
                                      <p key={index}>- {customization.AttributeName}</p>
                                    ))}
                                  </div>
                                )}
                                {item.ProductAttributeDetails.Addons && item.ProductAttributeDetails.Addons.length > 0 && (
                                  <>
                                    <button type="button" className="product-addons-button" onClick={() => {
                                      setHiddenAddons(prev => {
                                        const next = new Set(prev);
                                        next.has(index) ? next.delete(index) : next.add(index);
                                        return next;
                                      });
                                    }}>
                                      {hiddenAddons.has(index) ? "Show Add Ons" : "Hide Add Ons"}
                                    </button>
                                    <div className="product-addons">
                                      {!hiddenAddons.has(index) && item.ProductAttributeDetails.Addons.map((addon, addonIndex) => (
                                        <p key={addonIndex}>[+${addon.PriceAdjustment}] {addon.AttributeName}</p>
                                      ))}
                                    </div>
                                  </>
                                )}
                        </div>
                      ))
                    ) : (
                      <p className="cart-empty">Your cart is empty</p>
                    )}
                    <span className="separator-checkout separator my-3" />
                  </div>
                </div>

                <div className="cart-summary">
                  <div className="cart-amounts">
                    <div className="cart-amounts-list mb-3 mt-3">
                      <span className="fw-500 fs-16 text-gray-500">Sub Total:</span>
                      <span className="fw-600 fs-16 text-gray-400">${subtotal.toFixed(2)}</span>
                    </div>

                    <div className="cart-amounts-list mb-3">
                      <span className="fw-500 fs-16 text-gray-500">Tax 15%:</span>
                      <span className="fw-600 fs-16 text-gray-400">${tax?.toFixed(2)}</span>
                    </div>
                    {/* 
                  <div className="cart-amounts-list mb-3">
                    <span className="fw-500 fs-16 text-gray-500">Delivery Fee:</span>
                    <span className="fw-600 fs-16 text-gray-400">${deliveryFee.toFixed(2)}</span>
                  </div> */}
                  </div>

                  <span className="separator-checkout separator my-3" />

                  <div className="cart-total cart-amounts-list">
                    <span className="fw-500 fs-18 text-gray-500">Grand Total:</span>
                    <span className="fw-600 fs-16 text-gray-400">${grandTotal.toFixed(2)}</span>
                  </div>

                  <span className="separator-checkout separator my-3" />

                  {/* Terms Agreement */}
                  <div className="checkout-consent text-center d-flex align-items-center justify-center mt-0 mb-3">
                    <input
                      name="checkoutConsent"
                      id="checkoutConsent"
                      className="input-checkbox"
                      type="checkbox"
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      required
                    />
                    <label htmlFor="checkoutConsent" className="fs-14 fw-400 text-gray-500">
                      <span>
                        I agree to the
                        <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-primary decoration-none mx-1">
                          Terms of Service
                        </Link>
                        and
                        <Link to="#" className="text-primary decoration-none mx-1">
                          Refund Policy
                        </Link>
                        <span className="text-danger">*</span>
                      </span>
                    </label>
                  </div>

                  {/* Place Order Button */}
                  {/* <button
                  type="submit"
                  disabled={!agreeTerms}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition ${
                    agreeTerms ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  PLACE ORDER
                </button> */}
                  <div className="place-order-box">
                    <button
                      type="submit"
                      disabled={cartItems.length === 0 || !agreeTerms || loading || loadingButton || !isContactInfoValid()}
                      className={`btn-order ${cartItems.length > 0 && agreeTerms && !loading && !loadingButton && isContactInfoValid() ? "place-order-btn" : "btn-disabled"}`}
                    >
                      PLACE ORDER
                    </button>
                  </div>

                  {/* Continue Shopping */}
                  <div className="text-center mt-4">
                    <Link to="/menu" className="mains-continue-shoping">
                      <span className="arrow-continue">←</span> CONTINUE TO ADD MORE ITEMS
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
        <CheckoutModal
          isOpen={showModal}
          toggle={() => {
            history("/account/current-orders");
            setModal(false);
          }}
          onCancel={() => {
            history("/menu");
            setModal(false);
          }}
        />
      </div>
      {cartItems.length === 0 && !loading && (
        <div className="text-center my-5">
          <Link to="/menu" className="text-primary fs-16 fw-500 decoration-none">
            Your cart is empty. <span style={{ textDecoration: "underline" }}>Browse products</span> to add items to your cart.
          </Link>
        </div>
      )}
    </div>
  );
}

export default CheckoutPage;
